import { CommonModule, AsyncPipe } from '@angular/common';
import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  PRIMARY_OUTLET,
  Router
} from '@angular/router';
import { DOCUMENT } from '@angular/common';
import {
  catchError,
  combineLatest,
  filter,
  map,
  of,
  startWith,
  switchMap,
  distinctUntilChanged
} from 'rxjs';

import { ScrollManagerDirective } from '../shared/utils/directives/scroll-manager.directive';
import { DynamicLoadDirective } from '../shared/directives/dynamic-load.directive';
import { RoutesEnum } from '../models/routes.enum';
import { DynamicSection } from '../models/dynamic-section';
import { TypePageFields } from '../models/contentful-content-types/page';
import { ContentfulService } from '../shared/services/contentful/contentful.service';
import { MetadataService } from '../shared/services/metadata/metadata.service';
import { LocalizationService } from '../shared/services/localization/localization.service';

@Component({
  selector: 'app-dynamic-page',
  standalone: true,
  imports: [CommonModule, AsyncPipe, DynamicLoadDirective, ScrollManagerDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *ngIf="sections$ | async as state">
      @switch (state.status) { @case ('loading') {
      <div class="p-10 text-center text-gray-500 animate-pulse">Inhalt wird geladen...</div>
      } @case ('error') {
      <div class="p-10 text-center text-red-600">Seite konnte nicht geladen werden.</div>
      } @case ('success') {
      <div appScrollManager class="page-shell">
        <div class="page-bg "></div>
        <ng-template *ngFor="let section of state.sections" [appDynamicLoad]="section"></ng-template>
      </div>
      } }
    </ng-container>
  `
})
export class DynamicPageComponent {
  private readonly router = inject(Router);
  private readonly contentfulService = inject(ContentfulService);
  private readonly metadataService = inject(MetadataService);
  private readonly route = inject(ActivatedRoute);
  private readonly document = inject(DOCUMENT);
  private readonly localizationService = inject(LocalizationService);

  /**
   * ✅ Nur neu laden, wenn sich der PRIMARY-Pfad ändert.
   * (Aux-Outlets wie (popup:newsletter) ändern router.url, sollen aber keinen Reload auslösen.)
   */
  readonly primaryPath$ = this.router.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    startWith(null),
    map(() => this.getPrimaryPathname()),
    distinctUntilChanged()
  );

  /**
   * sections$ liefert den Ladezustand, Fehler oder geladene Sections.
   */
  readonly sections$ = combineLatest([
    this.localizationService.locale$.pipe(startWith('de-DE')),
    this.primaryPath$
  ]).pipe(
    switchMap(([locale, pathname]) =>
      this.contentfulService.getPage(pathname, locale).pipe(
        map((page) => {
          this.metadataService.setMetadata(page.metadata?.fields);
          return {
            status: 'success' as const,
            sections: this.mapSectionsAndDataFromPage(page)
          };
        }),
        startWith({ status: 'loading' as const, sections: [] }),
        catchError((err) => {
          console.error('❌ Fehler beim Laden der Seite:', err, { pathname });
          this.router.navigate([RoutesEnum.NotFound]);
          return of({ status: 'error' as const, sections: [] });
        })
      )
    )
  );

  /**
   * Liefert den Pfad des PRIMARY_OUTLET als pathname (z.B. "/home"),
   * ohne Auxiliary-Outlets (z.B. "(popup:newsletter)").
   */
  private getPrimaryPathname(): string {
    // Robust: aus router.url parsen, primary outlet extrahieren
    const tree = this.router.parseUrl(this.router.url);
    const group = tree.root.children[PRIMARY_OUTLET];

    const segments = group?.segments?.map((s) => s.path) ?? [];
    const path = '/' + segments.join('/');

    // Fallback: document.pathname ohne aux (für Safety)
    if (path === '/' && (this.document.location?.pathname ?? '') !== '/') {
      const p = this.document.location.pathname ?? '/';
      const primary = p.split('(')[0] || '/';
      return primary.endsWith('/') && primary.length > 1 ? primary.slice(0, -1) : primary;
    }

    return path;
  }

  private mapSectionsAndDataFromPage(page: TypePageFields): DynamicSection[] {
    if (!page?.sections?.length) return [];
    return page.sections.map(({ sys, fields }) => ({
      id: sys.contentType.sys.id,
      data: fields
    }));
  }
}